import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate, useParams, Outlet, useNavigate } from 'react-router-dom';
import ReactGA from 'react-ga4';
import { useTranslation } from 'react-i18next';
import './i18n/init';
import { SUPPORTED_LANGS, DEFAULT_LANG, getLangFromPath, stripLangPrefix, buildPathWithLang } from './i18n/lang';
import i18n from './i18n/init';
import Register from './Register';
import SignIn from './SignIn';
import AdsServicePage from './pages/AdsServicePage';
import AdsReachServicePage from './pages/AqhServicePage';
import ReachServicePage from './pages/ReachServicePage';
import AdrServicePage from './pages/AdrServicePage';
import GeoServicePage from './pages/GeoServicePage';
import ListenersServicePage from './pages/ListenersServicePage';
import LiveServicePage from './pages/LiveServicePage';
import LoyaltyServicePage from './pages/LoyaltyServicePage';
import NewListenersServicePage from './pages/NewListenersServicePage';
import PeakServicePage from './pages/PeakServicePage';
import AhaServicePage from './pages/AhaServicePage';
import RetentionServicePage from './pages/RetentionServicePage';
import Top40ServicePage from './pages/Top40ServicePage';
import TslServicePage from './pages/TslServicePage';
import TlhServicePage from './pages/TlhServicePage';
import UserAgentServicePage from './pages/UserAgentServicePage';
import LtrServicePage from './pages/LtrServicePage';
import QuartilesLtrServicePage from './pages/QuartilesLtrServicePage';
import Home from './pages/Home';
import AboutProject from "./pages/AboutProject";
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import NewsPage from './pages/NewsPage';
import ApiPage from './pages/ApiPage';
import {
  KnowledgeBaseArticlePage,
  KnowledgeBaseCategoryPage,
  KnowledgeBasePage,
} from './pages/KnowledgeBasePage';
import Statistics from './Statistics';
import StatsOverviewPage from './pages/StatsOverviewPage';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './ResetPassword';
import Sidebar from './components/Sidebar';
import AuthButton from './components/AuthButton';
import LangSwitcher, { LANG_SWITCHER_BUTTON_STYLE } from './components/LangSwitcher';
import LangLink from './components/LangLink';
import SeoTags from './components/SeoTags';
import ProfilePage from './pages/ProfilePage';
import RadioPage from './pages/RadioPage';
import RadioPortalPage from './pages/RadioPortalPage';
import LiveWallSettingsPage from './pages/LiveWallSettingsPage';
import ScheduleImportPage from './pages/ScheduleImportPage';
import NotFound from './pages/NotFound';
import SubscribeConfirmed from './pages/SubscribeConfirmed';
import Unsubscribe from './pages/Unsubscribe';
import SilenceSettingsPage from './pages/SilenceSettingsPage';
import SilenceLevelsPage from './pages/SilenceLevelsPage';
import SilenceOverviewPage from './pages/SilenceOverviewPage';
import BillingPage from './pages/BillingPage';
import PaymentSuccess from './pages/PaymentSuccess';
import CartButton from './components/CartButton';
import RadioButton from './components/RadioButton';
import CartModal from './components/CartModal';
import DemoBlockModal from './components/DemoBlockModal'; // ⬅️ добавили
import DemoAccessModal from './components/DemoAccessModal';
import TestAccessModal from './components/TestAccessModal';
import FloatingSideButtons from './components/FloatingSideButtons';
import API_BASE_URL from './api';
import { authFetch } from './authFetch';
import { useAuth } from './authContext';
import LiveWallPage from './pages/LiveWallPage';

const logoSrc = "/RadiomonitorNetLogo2025.png";
// === Normalize /:lang → /:lang/ (каноничный хвостовой слэш), без изменения UI ===
function LangRootRedirect() {
  const loc = useLocation();
  const { lang = '' } = useParams();
  const l = String(lang).toLowerCase();
  const exact = `/${l}`;
  if (SUPPORTED_LANGS.includes(l) && loc.pathname === exact) {
    return <Navigate to={`${exact}/` + (loc.search || '') + (loc.hash || '')} replace />;
  }
  return <Outlet />;
}
// === Page Title (i18n) — без изменения UI ===
function usePageTitle() {
  const { useTranslation } = require('react-i18next');
  const { t } = useTranslation('common');
  const loc = useLocation();

  const routeKey = React.useMemo(() => {
    const parts = loc.pathname.split('/').filter(Boolean);
    const head = parts[0]?.toLowerCase();
    const tail = SUPPORTED_LANGS.includes(head) ? parts.slice(1) : parts;
    const p = '/' + tail.join('/');

    if (p === '/' || p === '') return 'home';
    if (p.startsWith('/payments/success')) return 'payments_success';
    if (p.startsWith('/register')) return 'register';
    if (p.startsWith('/login')) return 'login';
    if (p.startsWith('/profile')) return 'profile';
    if (p.startsWith('/billing')) return 'billing';
    if (p.startsWith('/cabinet/schedule-import')) return 'schedule_import';
    if (p.startsWith('/cabinet/radio-portal')) return 'radio_portal';
    if (p.startsWith('/cabinet/radio')) return 'radio';

    if (p.startsWith('/service/stats')) return 'stats_overview';
    if (p.startsWith('/service/adr')) return 'adr';
    if (p.startsWith('/service/ads')) return 'ads';
    if (p.startsWith('/service/ads-reach')) return 'ads_reach';
    if (p.startsWith('/service/reach')) return 'reach';
    if (p.startsWith('/service/geo')) return 'geo';
    if (p.startsWith('/service/listeners')) return 'listeners';
    if (p.startsWith('/service/user-agent')) return 'user_agent';
    if (p.startsWith('/service/live')) return 'live';
    if (p.startsWith('/service/loyalty')) return 'loyalty';
    if (p.startsWith('/service/new-listeners')) return 'new_listeners';
    if (p.startsWith('/service/peak')) return 'peak';
    if (p.startsWith('/service/aha')) return 'aha';
    if (p.startsWith('/service/retention')) return 'retention';
    if (p.startsWith('/service/top40')) return 'top40';
    if (p.startsWith('/service/ltr')) return 'ltr';
    if (p.startsWith('/service/quartiles-ltr')) return 'quartiles_ltr';
    if (p.startsWith('/service/tsl')) return 'tsl';
    if (p.startsWith('/service/tlh')) return 'tlh';

    if (p.startsWith('/service/silence/settings')) return 'silence_settings';
    if (p.startsWith('/service/silence/levels')) return 'silence_levels';
    if (p.startsWith('/service/silence')) return 'silence_overview';

    if (p.startsWith('/about')) return 'about';
    if (p.startsWith('/news')) return 'news';
    if (p.startsWith('/api')) return 'api';
    if (p.startsWith('/knowledge-base')) return 'knowledge_base';
    if (p.startsWith('/faq')) return 'knowledge_base';
    if (p.startsWith('/terms')) return 'terms';
    if (p.startsWith('/privacy')) return 'privacy';
    if (p.startsWith('/reset-password')) return 'reset_password';
    if (p.startsWith('/forgot-password')) return 'forgot_password';
    if (p.startsWith('/statistics')) return 'statistics';

    return 'home';
  }, [loc.pathname]);

  React.useEffect(() => {
    const title = t(`routeTitles.${routeKey}`);
    const brand = t('app.title');
    document.title = title ? `${title} — ${brand}` : brand;
  }, [routeKey, loc.pathname, t]);
}

// === Legacy → /uk/... redirect (сохраняем query/hash), без изменения UI ===
function LegacyRedirect({ toPath }) {
  const loc = useLocation();
  const search = loc.search || "";
  const hash = loc.hash || "";
  const to = `/uk${toPath.startsWith("/") ? "" : "/"}${toPath}${search}${hash}`.replace(/\/{2,}/g, "/");
  return <Navigate to={to} replace />;
}

function FaqRedirect() {
  const loc = useLocation();
  const { lang = DEFAULT_LANG } = useParams();
  const to = buildPathWithLang(lang, '/knowledge-base', loc.search || '', loc.hash || '');
  return <Navigate to={to} replace />;
}


function LangGuard() {
  const { lang = '' } = useParams();
  const normalized = String(lang).toLowerCase();
  if (!SUPPORTED_LANGS.includes(normalized)) {
    const { pathname, search, hash } = window.location;
    const tail = stripLangPrefix(pathname);
    const to = buildPathWithLang(DEFAULT_LANG, tail, search, hash);
    return <Navigate to={to} replace />;
  }
  return <Outlet />;
}

function PublicLiveWallRoute() {
  useLangSync();
  return <LiveWallPage />;
}

function useLangSync() {
  const loc = useLocation();
  useEffect(() => {
    const lang = getLangFromPath(loc.pathname);
    // html lang (для SEO/a11y)
    if (document?.documentElement) {
      document.documentElement.setAttribute('lang', lang);
    }
    // внутренний стейт i18n (переводы подключим на следующих шагах)
    if (i18n.language !== lang) {
      i18n.changeLanguage(lang).catch(() => {});
    }
  }, [loc.pathname]);
}

// === GA4 pageview on SPA route change ===
function GAListener() {
  const loc = useLocation();
  useEffect(() => {
    // фиксируем язык интерфейса как user property (без PII)
    try {
      ReactGA.set({
        userProperties: {
          app_lang: getLangFromPath(loc.pathname) || 'uk'
        }
      });
    } catch {}

    // отправляем pageview c явным page_title
    try {
      ReactGA.send({
        hitType: 'pageview',
        page: loc.pathname + (loc.search || '') + (loc.hash || ''),
        title: document?.title || ''
      });
    } catch {}
  }, [loc.pathname, loc.search, loc.hash]);
  return null;
}

function AppContent() {
  // === NEW: mobile sidebar toggle ===
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const toggleSidebar = () => setSidebarOpen((v) => !v);
  const closeSidebar = () => setSidebarOpen(false);

  const sidebarRef = React.useRef(null);
  const mainRef = React.useRef(null);

  const headerRef = React.useRef(null);

  const { t } = useTranslation('common');
  const navigate = useNavigate();

  useLangSync();

  // ======= useState — всегда первыми =======
  const [cartItems, setCartItems] = useState([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const { isAuth, user } = useAuth();
  // === DEMO modal ===
  const [demoOpen, setDemoOpen] = useState(false);
  const [demoAction, setDemoAction] = useState("");
  const [demoAccessOpen, setDemoAccessOpen] = useState(false);
  const [testAccessOpen, setTestAccessOpen] = useState(false);
  useEffect(() => {
    const onDemoBlock = (e) => {
      const detail = e?.detail || {};
      setDemoAction(String(detail.action || ""));
      setDemoOpen(true);
    };
    window.addEventListener("demo:block", onDemoBlock);
    const onDemoAccess = () => setDemoAccessOpen(true);
    window.addEventListener("radiomonitor:demo-open", onDemoAccess);
    const onTestAccess = () => setTestAccessOpen(true);
    window.addEventListener("radiomonitor:test-open", onTestAccess);
    return () => {
      window.removeEventListener("demo:block", onDemoBlock);
      window.removeEventListener("radiomonitor:demo-open", onDemoAccess);
      window.removeEventListener("radiomonitor:test-open", onTestAccess);
    };
  }, []);
  // GA4: фиксируем события Login / Logout один раз при смене статуса
  const prevAuthRef = React.useRef(isAuth);
  useEffect(() => {
    if (prevAuthRef.current !== isAuth) {
      try {
        ReactGA.event({
          category: 'Auth',
          action: isAuth ? 'Login' : 'Logout',
          label: (user?.email || user?.name || '').toString().slice(0, 128)
        });
      } catch {}
      prevAuthRef.current = isAuth;
    }
  }, [isAuth, user]);
  const isUiOverlaid = isCartOpen || sidebarOpen || demoOpen || demoAccessOpen || testAccessOpen;

  // === UPDATED: единый лок для скролла при открытом сайдбаре, модалке корзины или DEMO ===
  React.useEffect(() => {
    const lock = sidebarOpen || isCartOpen || demoOpen || demoAccessOpen || testAccessOpen;
    const prevOverflow = document.body.style.overflow;
    const prevTouch = document.body.style.touchAction;
    if (lock) {
      document.body.style.overflow = 'hidden';
      document.body.style.touchAction = 'none';
    } else {
      document.body.style.overflow = prevOverflow || '';
      document.body.style.touchAction = prevTouch || '';
    }
    return () => {
      document.body.style.overflow = prevOverflow || '';
      document.body.style.touchAction = prevTouch || '';
    };
  }, [sidebarOpen, isCartOpen, demoOpen, demoAccessOpen, testAccessOpen]);

  // === NEW: измеряем высоту хедера и публикуем в CSS-переменную --header-h ===
  React.useLayoutEffect(() => {
    const el = headerRef.current;
    function applyHeight() {
      const h = el?.offsetHeight ?? 60;
      document.documentElement.style.setProperty('--header-h', `${h}px`);
    }
    applyHeight();
    const ro = new ResizeObserver(applyHeight);
    if (el) ro.observe(el);
    window.addEventListener('resize', applyHeight);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', applyHeight);
    };
  }, []);

  // ... внутри компонента App(), сразу под стейтами cartItems / isCartOpen:
  const API = (API_BASE_URL || "").replace(/\/+$/, "");
  const cartLoadingRef = React.useRef(false);

  const loadCart = React.useCallback(async () => {
    if (cartLoadingRef.current) return;
    if (!isAuth) {
      setCartItems([]);
      return;
    }
    try {
      cartLoadingRef.current = true;
      const res = await authFetch(`${API}/cart`);
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        const items = data?.items || [];
        setCartItems(items);
      } else {
        setCartItems([]);
      }
    } catch {
      setCartItems([]);
    } finally {
      cartLoadingRef.current = false;
    }
  }, [API, isAuth]);

  async function handleRemove(itemId) {
    if (!isAuth) {
      alert(t('auth.need_login'));
      return;
    }

    try {
      const res = await authFetch(`${API}/cart/item/${itemId}`, {
        method: "DELETE"
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        const msg = (data && (data.detail || data.message)) || t('cart.remove_error');
        alert(typeof msg === "string" ? msg : JSON.stringify(msg));
        return;
      }

      const items = data?.items || [];
      setCartItems(items);
      if (items.length === 0) setIsCartOpen(false); // закриваємо модалку, якщо кошик порожній
      // GA4: фиксируем удаление позиции из корзины
      try {
        ReactGA.event({
          category: 'Cart',
          action: 'RemoveItem',
          label: String(itemId).slice(0, 64),
          value: Array.isArray(items) ? items.length : 0
        });
      } catch {}
    } catch {
      alert(t('common.network_error'));
    }
  }

  // підвантажувати корзину при відкритті модалки
  React.useEffect(() => {
    if (isCartOpen) loadCart();
  }, [isCartOpen, loadCart]);

  React.useEffect(() => {
    function onCartRefresh(e) {
      const shouldOpen = Boolean(e?.detail?.openCart);
      loadCart().finally(() => {
        if (shouldOpen) setIsCartOpen(true);
      });
    }
    window.addEventListener('cart:refresh', onCartRefresh);
    return () => window.removeEventListener('cart:refresh', onCartRefresh);
  }, [loadCart]);

  // ініціалізація кошика при старті застосунку
  React.useEffect(() => {
    if (isAuth) {
      loadCart();
    } else {
      setCartItems([]);
    }
  }, [isAuth, loadCart]);

  // Поллинг кошика с паузой у фоні та миттєвим рефрешем при фокусі
  useEffect(() => {
    const tick = () => {
      if (document.hidden) return;
      if (!isAuth) return;
      loadCart();
    };
    const id = setInterval(tick, 10000); // 10s
    const onVis = () => { if (!document.hidden) tick(); };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [isAuth, loadCart]);
  const userName = user?.name || user?.email || '';

  // === NEW: авто-закрытие мобильного сайдбара при смене маршрута ===
  const loc = useLocation();
  React.useEffect(() => {
    if (isAuth) {
      loadCart();
    }
  }, [loc, isAuth, loadCart]);

  const currentLang = getLangFromPath(loc.pathname);
  const isHomeRoute = React.useMemo(() => {
    const normalized = (loc.pathname || "/").replace(/\/+$/, "/");
    if (normalized === "/" || normalized === "") return true;
    const tail = stripLangPrefix(normalized).replace(/\/+$/, "/");
    return tail === "/" || tail === "";
  }, [loc.pathname]);
  const isWideContainerRoute = React.useMemo(() => {
    const normalized = (loc.pathname || '/').toLowerCase();
    const tail = stripLangPrefix(normalized).replace(/\/+$/, '/');
    return tail.startsWith('/service/quartiles-ltr');
  }, [loc.pathname]);
  usePageTitle();
  React.useEffect(() => {
    setSidebarOpen((open) => (open ? false : open));
  }, [loc]);

  // === NEW: авто-закрытие корзины при смене маршрута ===
  React.useEffect(() => {
    setIsCartOpen((open) => (open ? false : open));
  }, [loc]);

  // === NEW: авто-фокус на основной контент после смены маршрута ===
  React.useEffect(() => {
    if (sidebarOpen || isCartOpen) return;
    const el = mainRef.current;
    if (el && typeof el.focus === 'function') el.focus();
  }, [loc, sidebarOpen, isCartOpen]);

  // === NEW: закрывать сайдбар по Esc на мобильных/десктопе ===
  React.useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setSidebarOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // === A11y: фокус и Tab-трап внутри мобильного сайдбара ===
  React.useEffect(() => {
    if (!sidebarOpen) return;
    const root = sidebarRef.current;
    if (!root) return;

    const prevActive = document.activeElement;

    const getFocusables = () =>
      Array.from(
        root.querySelectorAll(
          'a, button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )
      ).filter((el) => !el.hasAttribute('disabled') && !el.getAttribute('aria-hidden'));

    const list = getFocusables();
    if (list.length) list[0].focus();
    else {
      root.setAttribute('tabindex', '-1');
      root.focus();
    }

    const onKey = (e) => {
      if (e.key !== 'Tab') return;
      const els = getFocusables();
      if (!els.length) {
        e.preventDefault();
        return;
      }
      const first = els[0];
      const last = els[els.length - 1];
      const active = document.activeElement;
      if (e.shiftKey) {
        if (active === first || !root.contains(active)) {
          last.focus();
          e.preventDefault();
        }
      } else if (active === last) {
        first.focus();
        e.preventDefault();
      }
    };

    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      if (prevActive && typeof prevActive.focus === 'function') prevActive.focus();
    };
  }, [sidebarOpen]);



  // --- ЧЕКАУТ: создать інвойс і відправити користувача на сторінку оплати ---
  async function handleCheckout() {
    if (!isAuth) {
      alert(t('auth.login_first'));
      return;
    }
    try {
      const resp = await authFetch(`${API}/cart/checkout`, { method: "POST" });
      if (!resp.ok) {
        const txt = await resp.text();
        throw new Error(txt || `HTTP ${resp.status}`);
      }
      const data = await resp.json();
      // пробуем найти URL страницы оплаты в ответе монобанка
      const url =
        data?.invoice?.pageUrl ||
        data?.invoice?.pageUrlShort ||
        data?.invoice?.invoiceUrl ||
        null;

      if (url) {
        // GA4: фиксируем переход на оплату
        try {
          const host = new URL(url).hostname;
          ReactGA.event({
            category: 'Checkout',
            action: 'RedirectToPayment',
            label: host
          });
        } catch {}
        window.location.href = url;
      } else {
        console.warn("Не знайшов URL інвойсу у відповіді:", data);
        alert(t('cart.invoice_created_missing_link'));
      }
    } catch (e) {
      // GA4: фиксируем ошибку оформления
      try {
        ReactGA.event({
          category: 'Checkout',
          action: 'Error',
          label: (e?.message || 'unknown').slice(0, 120)
        });
      } catch {}
      console.error("Checkout error:", e);
      alert(t('cart.invoice_create_error'));
    }
  }

  return (
    <div className={`layout ${sidebarOpen ? 'sidebar-open' : ''}`}>
      <a href="#main-content" className="skip-link">{t('a11y.skip_to_main')}</a>
      {/* Header */}
      <header className="header" aria-hidden={isCartOpen} inert={isCartOpen ? '' : undefined}>
        <div
          ref={headerRef}
          style={{
            width: '100%',
            minHeight: 60,
            height: 'auto',
            background: 'rgb(0,110,69)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0 40px',
            boxSizing: 'border-box',
            flexWrap: 'wrap'
          }}
        >
          {/* === NEW: гамбургер на мобильных === */}
          <button
            type="button"
            aria-label={t('a11y.menu')}
            aria-expanded={sidebarOpen}
            aria-controls="app-sidebar"
            onClick={toggleSidebar}
            className="btn-hamburger"
            style={{
              display: 'none',
              marginRight: 12,
              background: 'transparent',
              border: 'none',
              width: 36,
              height: 36,
              cursor: 'pointer'
            }}
          >
            <span style={{ display: 'block', width: 22, height: 2, background: '#fff', margin: '6px 0' }} />
            <span style={{ display: 'block', width: 22, height: 2, background: '#fff', margin: '6px 0' }} />
            <span style={{ display: 'block', width: 22, height: 2, background: '#fff', margin: '6px 0' }} />
          </button>

          <LangLink
            to="/"
            style={{ display: 'inline-flex', alignItems: 'center' }}
          >
            <img src={logoSrc} alt="Radiomonitor" style={{ height: 38, marginRight: 10 }} />
          </LangLink>

          {/* Правый блок: корзина (если есть) → выбор радіо (если есть) → логин */}
          <div className="cart-and-login" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <CartButton
              count={cartItems.length}
              onClick={() => {
                try {
                  ReactGA.event({ category: 'Cart', action: 'Open' });
                } catch {}
                setIsCartOpen(true);
                loadCart();
              }}
            />
            <RadioButton
              className=""
              isLoggedIn={isAuth}
              onChange={() => {}}
            />
            <AuthButton isAuth={isAuth} userName={userName} />
          </div>

            
        </div>
      </header>

      {/* SEO: canonical + hreflang (без изменений верстки) */}
      <SeoTags />

      {/* Sidebar */}
      <aside
        id="app-sidebar"
        role="navigation"
        aria-label={t('a11y.menu')}
        className={`sidebar ${sidebarOpen ? 'open' : ''}`}
        aria-hidden={isCartOpen}
        inert={isCartOpen ? '' : undefined}
        onClick={(e) => e.stopPropagation()}
        ref={sidebarRef}
        tabIndex={-1}
      >
        <Sidebar isAuth={isAuth} />
      </aside>

      {/* === NEW: бэкдроп для закрытия сайдбара на мобильных === */}
      {sidebarOpen && <div className="sidebar-backdrop" onClick={closeSidebar} />}

      {/* Main */}
      <main
        id="main-content"
        className="main"
        role="main"
        aria-hidden={isUiOverlaid}
        inert={isUiOverlaid ? '' : undefined}
        ref={mainRef}
        tabIndex={-1}
      >
        <div
          className={`container ${isHomeRoute ? 'container-home' : ''} ${
            isWideContainerRoute ? 'container-wide' : ''
          }`}
        >
          <Routes>
            {/* normalize /:lang → /:lang/ */}
            <Route path="/:lang" element={<LangRootRedirect />} />

            {/* Префиксные маршруты: /:lang/... */}
            <Route path="/:lang" element={<LangGuard />}>
              <Route index element={<Home isAuth={isAuth} />} />
              <Route path="payments/success" element={<PaymentSuccess />} />
              <Route path="register" element={<Register />} />
              <Route path="login" element={<SignIn />} />
              <Route path="service/ads" element={<AdsServicePage />} />
              <Route path="service/ads-reach" element={<AdsReachServicePage />} />
              <Route path="service/reach" element={<ReachServicePage />} />
              <Route path="service/geo" element={<GeoServicePage />} />
              <Route path="service/listeners" element={<ListenersServicePage />} />
              <Route path="service/user-agent" element={<UserAgentServicePage />} />
              <Route path="service/live" element={<LiveServicePage />} />
              <Route path="service/loyalty" element={<LoyaltyServicePage />} />
              <Route path="service/new-listeners" element={<NewListenersServicePage />} />
              <Route path="service/adr" element={<AdrServicePage />} />
              <Route path="service/peak" element={<PeakServicePage />} />
              <Route path="service/aha" element={<AhaServicePage />} />
              <Route path="service/retention" element={<RetentionServicePage />} />
              <Route path="service/top40" element={<Top40ServicePage />} />
              <Route path="service/ltr" element={<LtrServicePage />} />
              <Route path="service/quartiles-ltr" element={<QuartilesLtrServicePage />} />
              <Route path="service/tsl" element={<TslServicePage />} />
              <Route path="service/tlh" element={<TlhServicePage />} />
              <Route path="service/stats" element={<StatsOverviewPage />} />
              <Route path="reset-password" element={<ResetPassword />} />
              <Route path="about" element={<AboutProject />} />
              <Route path="news" element={<NewsPage />} />
              <Route path="api" element={<ApiPage />} />
              <Route path="faq" element={<FaqRedirect />} />
              <Route path="knowledge-base" element={<KnowledgeBasePage />} />
              <Route path="knowledge-base/articles/:articleSlug" element={<KnowledgeBaseArticlePage />} />
              <Route path="knowledge-base/:categorySlug" element={<KnowledgeBaseCategoryPage />} />
              <Route path="statistics" element={<Statistics />} />
              <Route path="terms" element={<TermsPage />} />
              <Route path="privacy" element={<PrivacyPage />} />
              <Route path="forgot-password" element={<ForgotPassword />} />
              <Route path="profile" element={<ProfilePage />} />
              <Route path="service/silence/settings" element={<SilenceSettingsPage setCartItems={setCartItems} />} />
              <Route path="service/silence/levels" element={<SilenceLevelsPage />} />
              <Route path="service/silence" element={<SilenceOverviewPage />} />
              <Route path="billing" element={<BillingPage />} />
              <Route path="cabinet/schedule-import" element={<ScheduleImportPage />} />
              <Route path="cabinet/radio-portal" element={<RadioPortalPage />} />
              <Route path="cabinet/live-wall" element={<LiveWallSettingsPage />} />
              <Route path="cabinet/radio" element={<RadioPage />} />
              <Route path="subscribe/confirmed" element={<SubscribeConfirmed />} />
              <Route path="unsubscribe" element={<Unsubscribe />} />
              <Route path="*" element={<NotFound />} />
            </Route>

            {/* === Legacy (без префикса) → редирект на /uk/... === */}
            <Route path="/"                         element={<LegacyRedirect toPath="/" />} />
            <Route path="/subscribe/confirmed"      element={<SubscribeConfirmed />} />
            <Route path="/unsubscribe"              element={<Unsubscribe />} />
            <Route path="/payments/success"         element={<LegacyRedirect toPath="/payments/success" />} />
            <Route path="/register"                 element={<LegacyRedirect toPath="/register" />} />
            <Route path="/login"                    element={<LegacyRedirect toPath="/login" />} />
            <Route path="/service/ads"              element={<LegacyRedirect toPath="/service/ads" />} />
            <Route path="/service/ads-reach"        element={<LegacyRedirect toPath="/service/ads-reach" />} />
            <Route path="/service/reach"            element={<LegacyRedirect toPath="/service/reach" />} />
            <Route path="/service/adr"              element={<LegacyRedirect toPath="/service/adr" />} />
            <Route path="/service/geo"              element={<LegacyRedirect toPath="/service/geo" />} />
            <Route path="/service/listeners"        element={<LegacyRedirect toPath="/service/listeners" />} />
            <Route path="/service/user-agent"      element={<LegacyRedirect toPath="/service/user-agent" />} />
            <Route path="/service/live"             element={<LegacyRedirect toPath="/service/live" />} />
            <Route path="/service/loyalty"          element={<LegacyRedirect toPath="/service/loyalty" />} />
            <Route path="/service/new-listeners"    element={<LegacyRedirect toPath="/service/new-listeners" />} />
            <Route path="/service/peak"             element={<LegacyRedirect toPath="/service/peak" />} />
            <Route path="/service/aha"              element={<LegacyRedirect toPath="/service/aha" />} />
            <Route path="/service/retention"        element={<LegacyRedirect toPath="/service/retention" />} />
            <Route path="/service/top40"            element={<LegacyRedirect toPath="/service/top40" />} />
            <Route path="/service/ltr"              element={<LegacyRedirect toPath="/service/ltr" />} />
            <Route path="/service/quartiles-ltr"    element={<LegacyRedirect toPath="/service/quartiles-ltr" />} />
            <Route path="/service/tsl"              element={<LegacyRedirect toPath="/service/tsl" />} />
            <Route path="/service/tlh"              element={<LegacyRedirect toPath="/service/tlh" />} />
            <Route path="/service/stats"            element={<LegacyRedirect toPath="/service/stats" />} />
            <Route path="/reset-password"           element={<LegacyRedirect toPath="/reset-password" />} />
            <Route path="/about"                    element={<LegacyRedirect toPath="/about" />} />
            <Route path="/news"                     element={<LegacyRedirect toPath="/news" />} />
            <Route path="/api"                      element={<LegacyRedirect toPath="/api" />} />
            <Route path="/faq"                      element={<LegacyRedirect toPath="/knowledge-base" />} />
            <Route path="/statistics"               element={<LegacyRedirect toPath="/statistics" />} />
            <Route path="/terms"                    element={<LegacyRedirect toPath="/terms" />} />
            <Route path="/privacy"                  element={<LegacyRedirect toPath="/privacy" />} />
            <Route path="/forgot-password"          element={<LegacyRedirect toPath="/forgot-password" />} />
            <Route path="/profile"                  element={<LegacyRedirect toPath="/profile" />} />
            <Route path="/service/silence/settings" element={<LegacyRedirect toPath="/service/silence/settings" />} />
            <Route path="/service/silence/levels"   element={<LegacyRedirect toPath="/service/silence/levels" />} />
            <Route path="/service/silence"          element={<LegacyRedirect toPath="/service/silence" />} />
            <Route path="/billing"                  element={<LegacyRedirect toPath="/billing" />} />
            <Route path="/cabinet/schedule-import"  element={<LegacyRedirect toPath="/cabinet/schedule-import" />} />
            <Route path="/cabinet/radio-portal"     element={<LegacyRedirect toPath="/cabinet/radio-portal" />} />
            <Route path="/cabinet/radio"            element={<LegacyRedirect toPath="/cabinet/radio" />} />
            {/* хвост: любые прочие безъязыковые пути → /uk/... */}
            <Route path="*"                         element={<LegacyRedirect toPath={window.location.pathname} />} />
          </Routes>
        </div>
      </main>

      <FloatingSideButtons hidden={isUiOverlaid} />

      {/* Footer */}
      <footer className="footer" aria-hidden={isUiOverlaid} inert={isUiOverlaid ? '' : undefined}>
        <div
          style={{
            width: '100%',
            minHeight: 90,
            height: 'auto',
            background: 'rgb(0,110,69)',
            color: '#fff',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            fontSize: 15,
            position: "relative"
          }}
        >
          <div
            style={{
              width: '100%',
              maxWidth: 3000,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              paddingLeft: 39,
              paddingRight: 40,
              height: 'auto'
            }}
          >
            <div style={{ position: "relative", top: -8 }}>
              {/* Лого + переключатель слева, в одну линию */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 22 }}>
                <LangLink
                  to="/"
                  style={{ display: 'inline-flex', alignItems: 'center' }}
                >
                  <img src={logoSrc} alt="Radiomonitor" style={{ height: 38, marginRight: 15 }} />
                </LangLink>

                {/* Двигаем только переключатели при необходимости */}
                <div style={{ marginTop: 25, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <LangSwitcher className="footer-lang" />
                  <button
                    type="button"
                    onClick={() => {
                      document.cookie = 'rm_frontend=new; Path=/; Max-Age=31536000; SameSite=Lax; Secure';
                      window.history.replaceState(null, '', '/');
                      window.location.reload();
                    }}
                    style={{ ...LANG_SWITCHER_BUTTON_STYLE, whiteSpace: 'nowrap' }}
                  >
                    Нова версія
                  </button>
                </div>
              </div>

              {/* Копирайт фиксированный */}
              <div style={{ fontSize: 12, marginTop: -11, marginLeft: 40 }}>
                © 2025 RADIOMONITOR.NET
              </div>
            </div>




            <div style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              fontSize: 15
            }}>
              <LangLink to="/news" style={{ color: "#fff", textDecoration: "none" }}>
                {t('footer.news')}
              </LangLink>
              <span style={{ fontSize: 10 }}>●</span>
              <LangLink to="/api" style={{ color: "#fff", textDecoration: "none" }}>
                API
              </LangLink>
              <span style={{ fontSize: 10 }}>●</span>
              <LangLink to="/knowledge-base" style={{ color: "#fff", textDecoration: "none" }}>
                {t('footer.knowledge_base_help')}
              </LangLink>
              <span style={{ fontSize: 10 }}>●</span>
              <LangLink to="/terms" style={{ color: "#fff", textDecoration: "none" }}>
                {t('footer.terms')}
              </LangLink>
              <span style={{ fontSize: 10 }}>●</span>
              <LangLink to="/privacy" style={{ color: "#fff", textDecoration: "none" }}>
                {t('footer.privacy')}
              </LangLink>
              <span style={{ fontSize: 10 }}>●</span>
              <a
                href="mailto:support@radiomonitor.net"
                style={{ color: "#fff", textDecoration: "none" }}
                onClick={() => {
                  try { ReactGA.event({ category: 'Contact', action: 'EmailSupportClick' }); } catch {}
                }}
              >
                {t('footer.email_support')}
              </a>
            </div>
          </div>
        </div>
      </footer>

      {/* Модалка DEMO-блокировок */}
      <DemoBlockModal
        open={demoOpen}
        action={demoAction}
        onClose={() => setDemoOpen(false)}
        registrationUrl={`/${currentLang}/register`}
      />

      <TestAccessModal
        open={testAccessOpen}
        onClose={() => setTestAccessOpen(false)}
        onRegister={() => {
          const targetLang = currentLang || DEFAULT_LANG;
          const targetPath = buildPathWithLang(targetLang, "/register");
          navigate(targetPath);
        }}
      />

      <DemoAccessModal
        open={demoAccessOpen}
        onClose={() => setDemoAccessOpen(false)}
      />

      {/* Модалка корзины */}
      {cartItems.length > 0 && (
        <CartModal
          open={isCartOpen}
          items={cartItems}
          onRemove={handleRemove}
          onClose={() => setIsCartOpen(false)}
          onCheckout={handleCheckout}
        />
      )}
    </div>
  );
}

export default function App() {
  return (
    <Router>
      <GAListener />
      <Routes>
        <Route path="/:lang/live-wall" element={<LangGuard />}>
          <Route path=":token" element={<PublicLiveWallRoute />} />
        </Route>
        <Route path="/live-wall/:token" element={<PublicLiveWallRoute />} />
        <Route path="*" element={<AppContent />} />
      </Routes>
    </Router>
  );
}
